import { useEffect, useState } from "react";
import { getPopularSearchTerms } from "@/api/search-history";
import {
  getCachedPopularSearches,
  saveCachedPopularSearches,
} from "./popular-search-cache-storage";
import { normalizePopularSearchItems } from "./popular-search-data";

const POPULAR_SEARCH_REQUEST = { hours: 12, limit: 10 };

// ⚠️ 받아 오는 쪽이 배열을 그대로 주기도 하고 { data: [...] } 로 감싸 주기도 한다.
// 한 쪽만 벗기면 조용히 빈 목록이 된다(대표님이 정한 「밀 것」이 안 뜨던 원인).
const fetchAndCachePopularSearches = async ({
  shouldIgnore,
  shouldMarkFailedOnError,
  setPopularSearches,
  setPopularSearchesFetchFailed,
}) => {
  try {
    const response = await getPopularSearchTerms(POPULAR_SEARCH_REQUEST);
    const nextPopularSearches = normalizePopularSearchItems(response?.data ?? response);
    if (shouldIgnore()) return;

    if (nextPopularSearches.length === 0) {
      if (shouldMarkFailedOnError()) {
        setPopularSearchesFetchFailed(true);
      }
      return;
    }

    saveCachedPopularSearches(nextPopularSearches);
    setPopularSearchesFetchFailed(false);
    setPopularSearches(nextPopularSearches);
  } catch (_) {
    if (!shouldIgnore() && shouldMarkFailedOnError()) {
      setPopularSearchesFetchFailed(true);
    }
  }
};

export const usePopularSearchCache = ({ searchInFocus, mobileOverlayOpen }) => {
  const [popularSearches, setPopularSearches] = useState(() =>
    getCachedPopularSearches()
  );
  const [popularSearchesFetchFailed, setPopularSearchesFetchFailed] =
    useState(false);

  useEffect(() => {
    let ignore = false;
    const hasCachedPopularSearches = getCachedPopularSearches().length > 0;

    fetchAndCachePopularSearches({
      hasCachedPopularSearches,
      shouldIgnore: () => ignore,
      shouldMarkFailedOnError: () => getCachedPopularSearches().length === 0,
      setPopularSearches,
      setPopularSearchesFetchFailed,
    });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!searchInFocus && !mobileOverlayOpen) return;

    let ignore = false;
    const cachedPopularSearches = getCachedPopularSearches();
    const hasCachedPopularSearches = cachedPopularSearches.length > 0;

    if (hasCachedPopularSearches) {
      setPopularSearches(cachedPopularSearches);
      setPopularSearchesFetchFailed(false);
    }

    fetchAndCachePopularSearches({
      hasCachedPopularSearches,
      shouldIgnore: () => ignore,
      shouldMarkFailedOnError: () => !hasCachedPopularSearches,
      setPopularSearches,
      setPopularSearchesFetchFailed,
    });

    return () => {
      ignore = true;
    };
  }, [searchInFocus, mobileOverlayOpen]);

  return {
    popularSearches,
    popularSearchesFetchFailed,
  };
};
